/**
 * TableExplorerDialog
 *
 * Dialog for selecting a table to open in Table Explorer.
 * Similar to TableDesignerDialog but for exploring/viewing data.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { BarChart3, Table, X } from 'lucide-react';
import { TableSelector, type ExplorerSelectedTable } from './TableSelector';
import type { TableInfo } from '../hooks/useAvailableTables';
import type { DatabaseEngineId } from '@/core/engines';

type TableExplorerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  database: string;
  engineId?: DatabaseEngineId;
  /** Table currently selected in the explorer (shown as quick-access option) */
  explorerSelectedTable?: ExplorerSelectedTable;
  /** Called when a table is selected */
  onSelectTable: (table: TableInfo) => void;
  /** Override the dialog title and icon. Defaults to "Table Explorer". */
  titleVariant?: 'explorer' | 'profile';
};

export function TableExplorerDialog({
  open,
  onOpenChange,
  connectionId,
  database: initialDatabase,
  engineId = 'mssql',
  explorerSelectedTable,
  onSelectTable,
  titleVariant = 'explorer',
}: TableExplorerDialogProps) {
  const isProfile = titleVariant === 'profile';
  const TitleIcon = isProfile ? BarChart3 : Table;
  const titleText = isProfile ? 'Profile Data' : 'Table Explorer';
  // Local database state - allows changing database within the dialog
  const [currentDatabase, setCurrentDatabase] = useState(initialDatabase);

  // Reset to initial database when dialog opens or initial database changes
  useEffect(() => {
    if (open) {
      setCurrentDatabase(initialDatabase);
    }
  }, [open, initialDatabase]);

  const handleTableSelect = (table: TableInfo) => {
    onSelectTable(table);
    onOpenChange(false);
  };

  const handleDatabaseChange = (newDatabase: string) => {
    setCurrentDatabase(newDatabase);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[90vw] max-h-[80vh] flex flex-col overflow-hidden" data-testid="table-explorer-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <TitleIcon className="h-5 w-5" />
            {titleText}
          </DialogTitle>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden">
          <TableSelector
            connectionId={connectionId}
            database={currentDatabase}
            engineId={engineId}
            onSelectTable={handleTableSelect}
            explorerSelectedTable={explorerSelectedTable}
            onDatabaseChange={handleDatabaseChange}
            className="h-full overflow-hidden"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
